import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, doc, addDoc,
  query, where, Timestamp, runTransaction 
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const OrderProductionTracker = ({ orderId, orderNumber, onClose, onRefresh }) => {
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [bomDetails, setBomDetails] = useState({});
  const [materialPayments, setMaterialPayments] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  useEffect(() => {
    fetchOrderData();
    fetchSuppliers();
  }, [orderId]);

  const fetchOrderData = async () => {
    setLoading(true);
    try {
      // Fetch order
      const orderQuery = query(collection(db, 'orders'), where('orderId', '==', orderId));
      const orderSnapshot = await getDocs(orderQuery);
      const orderData = orderSnapshot.docs[0]?.data();
      setOrder(orderData);
      
      // Fetch client
      if (orderData?.clientId) {
        const clientQuery = query(collection(db, 'clients'), where('clientId', '==', orderData.clientId));
        const clientSnapshot = await getDocs(clientQuery);
        const clientData = clientSnapshot.docs[0]?.data();
        setClient(clientData);
      }

      // Fetch order items
      const itemsQuery = query(collection(db, 'orderItems'), where('orderId', '==', orderId));
      const itemsSnapshot = await getDocs(itemsQuery);
      const items = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrderItems(items);

      // Fetch BOM for each product
      const bomMap = {};
      const paymentsMap = {};

      for (const item of items) {
        const bomQuery = query(collection(db, 'bom'), where('productId', '==', item.productId));
        const bomSnapshot = await getDocs(bomQuery);
        const bomItems = bomSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const costPerItem = {};
        bomItems.forEach(bom => {
          const totalCost = (bom.quantityPerUnit || 0) * (bom.unitCost || 0);
          costPerItem[bom.materialName] = {
            quantity: bom.quantityPerUnit,
            unitCost: bom.unitCost,
            totalCost: totalCost * (item.quantity || 1),
            materialId: bom.id,
            productId: item.productId,
            productName: item.productName
          };
        });
        
        bomMap[item.productId] = costPerItem;
        
        // Fetch material payments
        const paymentQuery = query(
          collection(db, 'materialPayments'),
          where('orderId', '==', orderId),
          where('productId', '==', item.productId)
        );
        const paymentSnapshot = await getDocs(paymentQuery);
        
        paymentSnapshot.docs.forEach(doc => {
          const payment = doc.data();
          paymentsMap[`${item.productId}-${payment.materialName}`] = payment;
        });
      }
      
      setBomDetails(bomMap);
      setMaterialPayments(paymentsMap);
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'suppliers'));
      const suppliersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSuppliers(suppliersList);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleAddSupplier = async (supplierData) => {
    try {
      const suppliersRef = collection(db, 'suppliers');
      await addDoc(suppliersRef, {
        name: supplierData.name,
        contact: supplierData.contact,
        email: supplierData.email,
        address: supplierData.address,
        materials: [selectedMaterial?.materialName],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      await fetchSuppliers();
      setShowAddSupplierModal(false);
      alert('Supplier added successfully!');
    } catch (error) {
      console.error('Error adding supplier:', error);
      alert('Error adding supplier. Please try again.');
    }
  };

  const handleMaterialPayment = async (paymentData) => {
    try {
      await runTransaction(db, async (transaction) => {
        const orderRef = doc(db, 'orders', orderId);
        const orderDoc = await transaction.get(orderRef);
        const currentOrder = orderDoc.data();
        
        if (!currentOrder) {
          throw new Error('Order not found');
        }
        
        const paymentRef = doc(collection(db, 'materialPayments'));
        transaction.set(paymentRef, {
          paymentId: paymentRef.id,
          orderId: orderId,
          orderNumber: orderNumber,
          productId: paymentData.productId,
          productName: paymentData.productName,
          materialName: paymentData.materialName,
          amount: paymentData.amount,
          quantity: paymentData.quantity,
          unitCost: paymentData.unitCost,
          supplierId: paymentData.supplierId,
          supplierName: suppliers.find(s => s.id === paymentData.supplierId)?.name || 'Unknown',
          paymentDate: Timestamp.now(),
          reference: paymentData.reference,
          status: 'paid',
          createdAt: Timestamp.now()
        });
        
        const currentProgress = currentOrder.productionProgress || {};
        const materialKey = `${paymentData.productId}-${paymentData.materialName}`;
        currentProgress[materialKey] = {
          paid: true,
          paidAmount: paymentData.amount,
          paidDate: Timestamp.now(),
          supplierId: paymentData.supplierId,
          supplierName: suppliers.find(s => s.id === paymentData.supplierId)?.name || 'Unknown',
          reference: paymentData.reference
        };
        
        transaction.update(orderRef, {
          productionProgress: currentProgress,
          updatedAt: Timestamp.now()
        });
      });
      
      await fetchOrderData();
      setShowPaymentModal(false);
      if (onRefresh) onRefresh();
      alert('Material payment recorded successfully!');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment: ' + error.message);
    }
  };

  const calculateTotalCost = () => {
    let total = 0;
    Object.values(bomDetails).forEach(productBom => {
      Object.values(productBom).forEach(material => {
        total += material.totalCost || 0;
      });
    });
    return total;
  };

  const calculatePaidAmount = () => {
    let paid = 0;
    Object.values(materialPayments).forEach(payment => {
      paid += payment.amount || 0;
    });
    return paid;
  };

  const calculateProgress = () => {
    const total = calculateTotalCost();
    const paid = calculatePaidAmount();
    return total > 0 ? (paid / total) * 100 : 0;
  };

  const getMaterialStatus = (productId, materialName) => {
    const key = `${productId}-${materialName}`;
    const payment = materialPayments[key];
    if (payment) return { status: 'paid', payment };
    return { status: 'pending', payment: null };
  };

  // Generate Progress Report
  const generateProgressReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const totalCost = calculateTotalCost();
    const paidAmount = calculatePaidAmount();
    const depositAmount = order?.balanceDue ? order.totalSellingPrice - order.balanceDue : 0;
    const remainingToPay = totalCost - paidAmount;
    const depositRemaining = depositAmount - paidAmount;
    const needsMoreMoney = depositRemaining < 0;
    
    // Header
    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('PRODUCTION PROGRESS REPORT', pageWidth / 2, 25, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 20, 50);
    doc.text(`Order Number: ${orderNumber || orderId}`, 20, 57);
    doc.text(`Client: ${client?.name || 'N/A'}`, 20, 64);
    if (client?.phone) doc.text(`Phone: ${client.phone}`, 20, 71);
    
    // Financial Summary
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 85, 170, 60, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('FINANCIAL SUMMARY', 25, 100);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Order Value: ${(order?.totalSellingPrice || 0).toLocaleString()} UGX`, 25, 112);
    doc.text(`Client Deposit Received: ${depositAmount.toLocaleString()} UGX`, 25, 120);
    doc.text(`Total Material Cost: ${totalCost.toLocaleString()} UGX`, 25, 128);
    doc.text(`Materials Paid: ${paidAmount.toLocaleString()} UGX`, 25, 136);
    doc.text(`Materials Pending: ${remainingToPay.toLocaleString()} UGX`, 25, 144);
    
    // Decision Section
    const decisionY = 155;
    doc.setFillColor(needsMoreMoney ? '#fef2f2' : '#d1fae5');
    doc.rect(20, decisionY, 170, 35, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(needsMoreMoney ? '#dc2626' : '#059669');
    doc.text(needsMoreMoney ? '⚠️ ACTION REQUIRED' : '✓ ON TRACK', 25, decisionY + 12);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    if (needsMoreMoney) {
      const shortfall = Math.abs(depositRemaining);
      doc.text(`Client deposit (${depositAmount.toLocaleString()} UGX) is insufficient for materials already paid (${paidAmount.toLocaleString()} UGX).`, 25, decisionY + 22);
      doc.text(`Request additional ${shortfall.toLocaleString()} UGX from client before continuing production.`, 25, decisionY + 30);
    } else {
      const remainingDeposit = depositRemaining;
      doc.text(`Client deposit covers all materials paid. Remaining deposit: ${remainingDeposit.toLocaleString()} UGX.`, 25, decisionY + 22);
      if (remainingToPay === 0) {
        doc.text('All materials paid! Ready to proceed with production.', 25, decisionY + 30);
      } else {
        doc.text(`Continue production. Pending materials: ${remainingToPay.toLocaleString()} UGX`, 25, decisionY + 30);
      }
    }
    
    // Paid Materials Table
    const paidMaterials = [];
    const pendingMaterials = [];
    
    Object.entries(bomDetails).forEach(([productId, materials]) => {
      const product = orderItems.find(i => i.productId === productId);
      Object.entries(materials).forEach(([materialName, details]) => {
        const { status, payment } = getMaterialStatus(productId, materialName);
        const materialData = {
          product: product?.productName || 'Unknown',
          material: materialName,
          quantity: Math.round(details.quantity * (product?.quantity || 1)),
          unitCost: details.unitCost,
          totalCost: details.totalCost,
          paidAmount: payment?.amount || 0,
          status: status === 'paid' ? 'Paid' : 'Pending'
        };
        
        if (status === 'paid') {
          paidMaterials.push(materialData);
        } else {
          pendingMaterials.push(materialData);
        }
      });
    });
    
    // Paid Materials Table
    let currentY = 205;
    if (paidMaterials.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [['Material', 'Product', 'Quantity', 'Unit Cost', 'Total', 'Paid Amount']],
        body: paidMaterials.map(m => [
          m.material,
          m.product,
          m.quantity.toString(),
          `${m.unitCost.toLocaleString()} UGX`,
          `${m.totalCost.toLocaleString()} UGX`,
          `${m.paidAmount.toLocaleString()} UGX`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        styles: { fontSize: 8 }
      });
      currentY = doc.lastAutoTable.finalY + 10;
    }
    
    // Pending Materials Table
    if (pendingMaterials.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [['Material', 'Product', 'Quantity', 'Unit Cost', 'Total Cost', 'Status']],
        body: pendingMaterials.map(m => [
          m.material,
          m.product,
          m.quantity.toString(),
          `${m.unitCost.toLocaleString()} UGX`,
          `${m.totalCost.toLocaleString()} UGX`,
          'Pending'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        styles: { fontSize: 8 }
      });
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('This report is auto-generated and shows current production progress.', pageWidth / 2, doc.internal.pageSize.height - 20, { align: 'center' });
    doc.text('For questions, contact the production manager.', pageWidth / 2, doc.internal.pageSize.height - 14, { align: 'center' });
    
    doc.save(`Production_Report_${orderNumber || orderId}.pdf`);
  };

  if (loading) {
    return React.createElement('div', { style: styles.loadingContainer },
      React.createElement('div', { style: styles.spinner }),
      React.createElement('p', null, 'Loading production tracker...')
    );
  }

  const totalCost = calculateTotalCost();
  const paidAmount = calculatePaidAmount();
  const progress = calculateProgress();
  const depositAmount = order?.balanceDue ? order.totalSellingPrice - order.balanceDue : 0;
  const needsMoreMoney = depositAmount < paidAmount;

  return React.createElement('div', { style: styles.container },
    React.createElement('div', { style: styles.header },
      React.createElement('div', null,
        React.createElement('h2', { style: styles.title }, 'Production Cost Tracker'),
        React.createElement('p', { style: styles.subtitle }, `Order: ${orderNumber || orderId}`)
      ),
      React.createElement('div', { style: { display: 'flex', gap: '12px' } },
        React.createElement('button', { 
          style: { ...styles.reportButton, background: '#3b82f6' }, 
          onClick: generateProgressReport 
        }, '📄 Print Report'),
        React.createElement('button', { style: styles.closeButton, onClick: onClose }, '×')
      )
    ),
    
    // Financial Alert Banner
    needsMoreMoney && React.createElement('div', { style: styles.alertBanner },
      React.createElement('span', { style: styles.alertIcon }, '⚠️'),
      React.createElement('div', null,
        React.createElement('strong', null, 'Action Required: '),
        `Client deposit (${depositAmount.toLocaleString()} UGX) is less than materials paid (${paidAmount.toLocaleString()} UGX). Request additional ${(paidAmount - depositAmount).toLocaleString()} UGX.`
      )
    ),
    
    React.createElement('div', { style: styles.progressSection },
      React.createElement('div', { style: styles.progressCard },
        React.createElement('div', { style: styles.progressLabel }, 'Production Progress'),
        React.createElement('div', { style: styles.progressBarContainer },
          React.createElement('div', { style: { ...styles.progressBar, width: `${progress}%` } })
        ),
        React.createElement('div', { style: styles.progressStats },
          React.createElement('span', null, `Paid: ${paidAmount.toLocaleString()} UGX`),
          React.createElement('span', null, `Total: ${totalCost.toLocaleString()} UGX`),
          React.createElement('span', null, `${progress.toFixed(1)}% Complete`)
        )
      )
    ),
    
    // Financial Comparison Cards
    React.createElement('div', { style: styles.financialGrid },
      React.createElement('div', { style: styles.financialCard },
        React.createElement('div', { style: styles.financialIcon }, '💰'),
        React.createElement('div', null,
          React.createElement('div', { style: styles.financialLabel }, 'Client Deposit'),
          React.createElement('div', { style: styles.financialValue }, `${depositAmount.toLocaleString()} UGX`)
        )
      ),
      React.createElement('div', { style: styles.financialCard },
        React.createElement('div', { style: styles.financialIcon }, '🏭'),
        React.createElement('div', null,
          React.createElement('div', { style: styles.financialLabel }, 'Materials Cost'),
          React.createElement('div', { style: styles.financialValue }, `${totalCost.toLocaleString()} UGX`)
        )
      ),
      React.createElement('div', { style: { ...styles.financialCard, ...(paidAmount > depositAmount ? styles.warningCard : styles.successCard) } },
        React.createElement('div', { style: styles.financialIcon }, '💵'),
        React.createElement('div', null,
          React.createElement('div', { style: styles.financialLabel }, 'Spent So Far'),
          React.createElement('div', { style: styles.financialValue }, `${paidAmount.toLocaleString()} UGX`)
        )
      ),
      React.createElement('div', { style: styles.financialCard },
        React.createElement('div', { style: styles.financialIcon }, '⚖️'),
        React.createElement('div', null,
          React.createElement('div', { style: styles.financialLabel }, 'Remaining Deposit'),
          React.createElement('div', { style: { ...styles.financialValue, color: depositAmount - paidAmount >= 0 ? '#10b981' : '#ef4444' } }, 
            `${(depositAmount - paidAmount).toLocaleString()} UGX`
          )
        )
      )
    ),
    
    React.createElement('div', { style: styles.materialsSection },
      React.createElement('h3', { style: styles.sectionTitle }, 'Materials & Cost Breakdown'),
      Object.keys(bomDetails).length === 0 ? 
        React.createElement('div', { style: styles.emptyState }, 
          'No materials found for this order. Please ensure:\n1. Products have Bill of Materials (BOM) set up\n2. Order items are properly linked to products'
        ) :
        Object.entries(bomDetails).map(([productId, materials]) => {
          const product = orderItems.find(i => i.productId === productId);
          return React.createElement('div', { key: productId, style: styles.productCard },
            React.createElement('div', { style: styles.productHeader },
              React.createElement('h4', { style: styles.productName }, product?.productName || 'Unknown Product'),
              React.createElement('span', { style: styles.productQuantity }, `x${product?.quantity || 1}`)
            ),
            React.createElement('div', { style: styles.materialsTable },
              React.createElement('div', { style: styles.tableHeader },
                React.createElement('div', { style: styles.colMaterial }, 'Material'),
                React.createElement('div', { style: styles.colQuantity }, 'Qty Needed'),
                React.createElement('div', { style: styles.colUnitCost }, 'Unit Cost'),
                React.createElement('div', { style: styles.colTotal }, 'Total Cost'),
                React.createElement('div', { style: styles.colStatus }, 'Status'),
                React.createElement('div', { style: styles.colAction }, 'Action')
              ),
              Object.entries(materials).map(([materialName, details]) => {
                const { status } = getMaterialStatus(productId, materialName);
                const materialCost = details.totalCost;
                const quantityNeeded = Math.round(details.quantity * (product?.quantity || 1));
                
                return React.createElement('div', { key: materialName, style: styles.tableRow },
                  React.createElement('div', { style: styles.colMaterial }, materialName),
                  React.createElement('div', { style: styles.colQuantity }, quantityNeeded),
                  React.createElement('div', { style: styles.colUnitCost }, `${details.unitCost.toLocaleString()} UGX`),
                  React.createElement('div', { style: styles.colTotal }, `${materialCost.toLocaleString()} UGX`),
                  React.createElement('div', { style: styles.colStatus },
                    status === 'paid' 
                      ? React.createElement('span', { style: styles.paidBadge }, '✓ Paid')
                      : React.createElement('span', { style: styles.pendingBadge }, '⏳ Pending')
                  ),
                  React.createElement('div', { style: styles.colAction },
                    status !== 'paid' && React.createElement('button', {
                      style: styles.payButton,
                      onClick: () => {
                        setSelectedMaterial({ 
                          productId, 
                          productName: product?.productName,
                          materialName, 
                          ...details,
                          quantityNeeded
                        });
                        setShowPaymentModal(true);
                      }
                    }, 'Pay Supplier')
                  )
                );
              })
            )
          );
        })
    ),
    
    showPaymentModal && React.createElement(PaymentModal, {
      material: selectedMaterial,
      suppliers: suppliers,
      orderNumber: orderNumber,
      onClose: () => setShowPaymentModal(false),
      onPay: handleMaterialPayment,
      onAddSupplier: () => setShowAddSupplierModal(true)
    }),
    
    showAddSupplierModal && React.createElement(AddSupplierModal, {
      materialName: selectedMaterial?.materialName,
      onClose: () => setShowAddSupplierModal(false),
      onAdd: handleAddSupplier
    })
  );
};

// Payment Modal Component
const PaymentModal = ({ material, suppliers, orderNumber, onClose, onPay, onAddSupplier }) => {
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [amount, setAmount] = useState(material?.totalCost || 0);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSupplier) {
      alert('Please select a supplier');
      return;
    }
    
    setLoading(true);
    await onPay({
      productId: material.productId,
      productName: material.productName,
      materialName: material.materialName,
      amount: parseFloat(amount),
      quantity: material.quantity,
      unitCost: material.unitCost,
      supplierId: selectedSupplier,
      reference: reference || `Payment for ${orderNumber} - ${material.materialName}`
    });
    setLoading(false);
  };

  return React.createElement('div', { style: modalStyles.overlay, onClick: onClose },
    React.createElement('div', { style: modalStyles.container, onClick: e => e.stopPropagation() },
      React.createElement('div', { style: modalStyles.header },
        React.createElement('h3', { style: modalStyles.title }, 'Pay Supplier'),
        React.createElement('button', { style: modalStyles.closeButton, onClick: onClose }, '×')
      ),
      React.createElement('form', { onSubmit: handleSubmit, style: modalStyles.form },
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Material'),
          React.createElement('input', { type: 'text', style: modalStyles.input, value: material?.materialName, disabled: true })
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Amount (UGX)'),
          React.createElement('input', { type: 'number', style: modalStyles.input, value: amount, onChange: e => setAmount(e.target.value), required: true })
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Supplier'),
          React.createElement('div', { style: { display: 'flex', gap: '8px' } },
            React.createElement('select', { style: { ...modalStyles.select, flex: 1 }, value: selectedSupplier, onChange: e => setSelectedSupplier(e.target.value), required: true },
              React.createElement('option', { value: '' }, 'Select Supplier'),
              suppliers.length === 0 ? React.createElement('option', { disabled: true }, 'No suppliers added yet') :
                suppliers.map(s => React.createElement('option', { key: s.id, value: s.id }, s.name))
            ),
            React.createElement('button', {
              type: 'button',
              style: { ...modalStyles.buttonSecondary, padding: '10px 16px', whiteSpace: 'nowrap' },
              onClick: onAddSupplier
            }, '+ New Supplier')
          )
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Reference'),
          React.createElement('input', { type: 'text', style: modalStyles.input, value: reference, onChange: e => setReference(e.target.value), placeholder: 'Invoice or payment reference' })
        ),
        React.createElement('div', { style: modalStyles.actions },
          React.createElement('button', { type: 'button', style: modalStyles.buttonSecondary, onClick: onClose }, 'Cancel'),
          React.createElement('button', { type: 'submit', style: modalStyles.buttonPrimary, disabled: loading || !selectedSupplier }, 
            loading ? 'Processing...' : 'Record Payment'
          )
        )
      )
    )
  );
};

// Add Supplier Modal Component
const AddSupplierModal = ({ materialName, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter supplier name');
      return;
    }
    
    setLoading(true);
    await onAdd(formData);
    setLoading(false);
  };

  return React.createElement('div', { style: modalStyles.overlay, onClick: onClose },
    React.createElement('div', { style: { ...modalStyles.container, maxWidth: '450px' }, onClick: e => e.stopPropagation() },
      React.createElement('div', { style: modalStyles.header },
        React.createElement('h3', { style: modalStyles.title }, 'Add New Supplier'),
        React.createElement('button', { style: modalStyles.closeButton, onClick: onClose }, '×')
      ),
      React.createElement('form', { onSubmit: handleSubmit, style: modalStyles.form },
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Supplier Name *'),
          React.createElement('input', {
            type: 'text',
            style: modalStyles.input,
            value: formData.name,
            onChange: e => setFormData({ ...formData, name: e.target.value }),
            placeholder: 'e.g., Timber Suppliers Ltd',
            required: true
          })
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Contact Number'),
          React.createElement('input', {
            type: 'tel',
            style: modalStyles.input,
            value: formData.contact,
            onChange: e => setFormData({ ...formData, contact: e.target.value }),
            placeholder: '+256 XXX XXX XXX'
          })
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Email'),
          React.createElement('input', {
            type: 'email',
            style: modalStyles.input,
            value: formData.email,
            onChange: e => setFormData({ ...formData, email: e.target.value }),
            placeholder: 'supplier@example.com'
          })
        ),
        React.createElement('div', { style: modalStyles.formGroup },
          React.createElement('label', { style: modalStyles.label }, 'Address'),
          React.createElement('textarea', {
            style: { ...modalStyles.input, resize: 'vertical', minHeight: '60px' },
            value: formData.address,
            onChange: e => setFormData({ ...formData, address: e.target.value }),
            placeholder: 'Supplier address'
          })
        ),
        materialName && React.createElement('div', { style: { ...modalStyles.formGroup, background: '#f8fafc', padding: '12px', borderRadius: '8px', marginBottom: '16px' } },
          React.createElement('span', { style: { fontSize: '12px', color: '#64748b' } }, 'This supplier will be associated with:'),
          React.createElement('div', { style: { fontWeight: '600', color: '#7f1a2e', marginTop: '4px' } }, materialName)
        ),
        React.createElement('div', { style: modalStyles.actions },
          React.createElement('button', { type: 'button', style: modalStyles.buttonSecondary, onClick: onClose }, 'Cancel'),
          React.createElement('button', { type: 'submit', style: modalStyles.buttonPrimary, disabled: loading }, 
            loading ? 'Adding...' : 'Add Supplier'
          )
        )
      )
    )
  );
};

// Styles
const styles = {
  container: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '85vh',
    background: '#ffffff',
    borderRadius: '24px',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    zIndex: 1000,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#64748b'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#7f1a2e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6',
    position: 'sticky',
    top: 0,
    background: '#ffffff',
    zIndex: 10
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8',
    transition: 'color 0.2s ease'
  },
  reportButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#ffffff',
    transition: 'all 0.2s ease'
  },
  alertBanner: {
    margin: '20px 24px',
    padding: '12px 16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#dc2626'
  },
  alertIcon: {
    fontSize: '20px'
  },
  progressSection: {
    padding: '24px',
    borderBottom: '1px solid #eef2f6'
  },
  progressCard: {
    background: '#f8fafc',
    borderRadius: '16px',
    padding: '20px'
  },
  progressLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '12px'
  },
  progressBarContainer: {
    height: '8px',
    background: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressBar: {
    height: '100%',
    background: '#7f1a2e',
    borderRadius: '4px',
    transition: 'width 0.3s ease'
  },
  progressStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '12px',
    fontSize: '12px',
    color: '#64748b'
  },
  financialGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    padding: '20px 24px',
    background: '#ffffff',
    borderBottom: '1px solid #eef2f6'
  },
  financialCard: {
    background: '#f8fafc',
    padding: '16px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  financialIcon: {
    fontSize: '28px'
  },
  financialLabel: {
    fontSize: '11px',
    color: '#64748b',
    marginBottom: '4px'
  },
  financialValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0f172a'
  },
  warningCard: {
    borderLeft: '3px solid #ef4444'
  },
  successCard: {
    borderLeft: '3px solid #10b981'
  },
  materialsSection: {
    padding: '24px'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '16px'
  },
  productCard: {
    background: '#ffffff',
    border: '1px solid #eef2f6',
    borderRadius: '16px',
    marginBottom: '20px',
    overflow: 'hidden'
  },
  productHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: '#fafcff',
    borderBottom: '1px solid #eef2f6'
  },
  productName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  productQuantity: {
    fontSize: '13px',
    color: '#64748b'
  },
  materialsTable: {
    padding: '16px 20px'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: '12px',
    padding: '10px 0',
    fontSize: '11px',
    fontWeight: '600',
    color: '#64748b',
    borderBottom: '1px solid #eef2f6'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: '12px',
    padding: '12px 0',
    fontSize: '13px',
    borderBottom: '1px solid #f1f5f9'
  },
  colMaterial: { fontSize: '13px', color: '#334155' },
  colQuantity: { textAlign: 'center', color: '#475569' },
  colUnitCost: { textAlign: 'right', color: '#475569' },
  colTotal: { textAlign: 'right', fontWeight: '500', color: '#1e293b' },
  colStatus: { textAlign: 'center' },
  colAction: { textAlign: 'center' },
  paidBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    background: '#d1fae5',
    color: '#059669',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500'
  },
  pendingBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    background: '#fef3c7',
    color: '#d97706',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500'
  },
  payButton: {
    padding: '6px 14px',
    background: '#7f1a2e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '11px',
    cursor: 'pointer',
    transition: 'background 0.2s ease'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: '#94a3b8',
    fontSize: '13px',
    whiteSpace: 'pre-line'
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100
  },
  container: {
    background: '#ffffff',
    borderRadius: '24px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  form: {
    padding: '24px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    background: '#f8fafc'
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  buttonPrimary: {
    flex: 1,
    background: '#7f1a2e',
    color: '#ffffff',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  buttonSecondary: {
    flex: 1,
    background: '#f1f5f9',
    color: '#334155',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

// Add animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default OrderProductionTracker;